import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, mergeProps as _mergeProps, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  "data-testid": "upload-modal-step-preview-card",
  class: "flex flex-col gap-y-4 py-2 h-full preview-step"
}
const _hoisted_2 = { class: "flex flex-col lg:flex-row gap-x-4" }
const _hoisted_3 = {
  key: 0,
  class: "text-lg font-bold text-textPrimary text-left truncate"
}
const _hoisted_4 = {
  key: 0,
  class: "bg-[rgba(22,22,29,0.16)] p-1 absolute top-2 right-2 z-10 flex flex-col rounded-md shadow-sm overflow-hidden gap-1"
}
const _hoisted_5 = { class: "border border-primary30 rounded-md" }
const _hoisted_6 = { class: "border border-primary30 rounded-md" }
const _hoisted_7 = { class: "border border-primary30 rounded-md" }
const _hoisted_8 = { class: "border border-primary30 rounded-md" }
const _hoisted_9 = { class: "border border-primary30 rounded-md" }
const _hoisted_10 = ["aria-pressed", "aria-label", "title", "disabled"]
const _hoisted_11 = { class: "border border-primary30 rounded-md" }
const _hoisted_12 = ["href"]
const _hoisted_13 = ["src"]
const _hoisted_14 = {
  key: 0,
  class: "w-full lg:w-[360px] flex flex-col text-left"
}
const _hoisted_15 = /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("h3", { class: "text-lg font-bold text-textPrimary mb-4" }, "Confirme a leitura de arquivo"),
  /*#__PURE__*/_createElementVNode("p", { class: "text-base text-neutral50 leading-relaxed mb-4" }, " Certifique-se de que é o arquivo correto e está rotacionado na orientação correta para leitura. "),
  /*#__PURE__*/_createElementVNode("p", { class: "text-base text-neutral50 leading-relaxed" }, " Caso necessário, use o botão abaixo para rotacionar a imagem. ")
], -1)
const _hoisted_16 = {
  key: 0,
  class: "flex flex-col gap-1 text-left mt-6"
}
const _hoisted_17 = /*#__PURE__*/_createElementVNode("span", { class: "text-base font-bold leading-6 tracking-[0.08px] text-[#16161D]" }, " Modalidade de registro ", -1)
const _hoisted_18 = { class: "text-sm text-textPrimary" }

import { ref, computed, watch, onUnmounted, nextTick, defineAsyncComponent } from 'vue'
import { mdiFullscreen, mdiRotateRight, mdiPlus, mdiMinus, mdiOpenInNew, mdiMagnify } from '@mdi/js'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  MAGNIFIER_HEIGHT,
  MAGNIFIER_ROTATION_TOOLTIP,
  MAGNIFIER_WIDTH,
  computeMagnifierGeometry,
  isMagnifierBlockedByRotation as magnifierBlockedByRotation
} from '@/modules/company/Approvals/DsAttendance/utils/sheet-magnifier'


export default /*#__PURE__*/_defineComponent({
  __name: 'StepPreview',
  props: {
    file: {},
    fileUrl: {},
    fileType: {},
    rotation: {},
    zoom: {},
    mode: {},
    showDisplayName: { type: Boolean, default: true },
    monitoringTypeLabel: { default: '' },
    page: {},
    magnifier: { type: Boolean },
    magnifierToggle: { type: Boolean },
    magnifierZoom: {},
    heightPx: {},
    hideToolbar: { type: Boolean }
  },
  emits: ["update:rotation", "update:zoom", "update:page", "update:totalPages", "update:magnifier", "image-error"],
  setup(__props: any, { emit }) {

const props = __props;

const PdfRenderer = defineAsyncComponent(() => import('./PdfRenderer.vue'))



const isPdf = computed(() => {
  if (props.file) return props.file.type === 'application/pdf'
  if (props.fileType) return props.fileType === 'application/pdf'
  if (props.fileUrl) return props.fileUrl.toLowerCase().includes('.pdf')
  return false
})

const displayName = computed(() => {
  if (props.file) return props.file.name
  if (props.fileUrl) {
    const parts = props.fileUrl.split('/')
    return parts[parts.length - 1] || props.fileUrl
  }
  return ''
})

/**
 * `update:totalPages` existe para o checklist do Eixo 2A: `expectedPages` é quantas páginas o
 * emissor anexou, e só este componente sabe quantas o PDF tem (imagem é sempre 1).
 */


const previewUrl = ref<string | null>(null)
const previewContainer = ref<HTMLElement | null>(null)
const scrollContainer = ref<HTMLElement | null>(null)
const imageRef = ref<HTMLImageElement | null>(null)

const currentPage = ref(1)
const totalPages = ref(1)
const pdfLoadError = ref(false)

watch(totalPages, (value) => emit('update:totalPages', value), { immediate: true })

/**
 * Página controlada de fora quando a prop vem definida: a tela de auditoria manda a página
 * da linha clicada, e o `update:page` do renderizador devolve a navegação pelas setas.
 */
watch(
  () => props.page,
  (page) => {
    if (typeof page === 'number' && page >= 1 && page !== currentPage.value)
      currentPage.value = page
  },
  { immediate: true }
)

const setPage = (page: number) => {
  currentPage.value = page
  emit('update:page', page)
  refreshMagnifierSource()
}

// --- Lupa 2× (cara-crachá V1 + lupa) -------------------------------------------------
/**
 * A lupa só opera ligada E sem rotação — com a folha rotacionada a geometria da lente aponta
 * para outra região (regra compartilhada com a tela de auditoria, que desabilita o botão).
 */
const isMagnifierActive = computed(
  () => props.magnifier === true && !magnifierBlockedByRotation(props.rotation)
)

/** Mesma regra do `isMagnifierActive`, isolada porque o botão precisa dela sem o "ligada". */
const isMagnifierBlocked = computed(() => magnifierBlockedByRotation(props.rotation))

const magnifierTooltip = computed(() => {
  if (isMagnifierBlocked.value) return MAGNIFIER_ROTATION_TOOLTIP
  return props.magnifier === true ? 'Desligar lupa (atalho L)' : 'Lupa 2× (atalho L)'
})

function toggleMagnifier(): void {
  if (isMagnifierBlocked.value) return
  emit('update:magnifier', props.magnifier !== true)
}

/** URL/data-URL do que a lente amplia; `null` enquanto não há fonte utilizável. */
const magnifierSource = ref<string | null>(null)
const lens = ref({
  visible: false,
  left: 0,
  top: 0,
  bgWidth: 0,
  bgHeight: 0,
  bgPosX: 0,
  bgPosY: 0
})

/**
 * PDF renderiza num canvas — a lente precisa de um bitmap. `toDataURL` lança em canvas
 * "tainted" (folha servida sem CORS): nesse caso a lupa simplesmente não acende, em vez de
 * derrubar a tela.
 */
function refreshMagnifierSource(): void {
  if (!isMagnifierActive.value) return
  if (!isPdf.value) {
    magnifierSource.value = previewUrl.value
    return
  }
  nextTick(() => {
    requestAnimationFrame(() => {
      const canvas = scrollContainer.value?.querySelector('canvas') ?? null
      if (!canvas) {
        magnifierSource.value = null
        return
      }
      try {
        magnifierSource.value = canvas.toDataURL('image/png')
      } catch (error) {
        console.warn('Lupa indisponível para esta folha (canvas sem permissão de leitura).', error)
        magnifierSource.value = null
      }
    })
  })
}

/** Retângulo da folha renderizada — a imagem quando há imagem, o canvas quando é PDF. */
function magnifierTarget(): HTMLElement | null {
  if (!isPdf.value) return imageRef.value
  return (scrollContainer.value?.querySelector('canvas') as HTMLElement | null) ?? null
}

function updateLens(point: { clientX: number; clientY: number }): void {
  const target = magnifierTarget()
  if (!target) {
    lens.value = { ...lens.value, visible: false }
    return
  }
  const rect = target.getBoundingClientRect()
  const geometry = computeMagnifierGeometry({
    mouseX: point.clientX,
    mouseY: point.clientY,
    rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
    zoom: props.magnifierZoom
  })
  const container = scrollContainer.value?.getBoundingClientRect()
  // A lente é filha do viewport, não do canvas: o offset do canvas dentro dele entra aqui.
  const offsetX = container ? rect.left - container.left : 0
  const offsetY = container ? rect.top - container.top : 0
  lens.value = {
    visible: geometry.visible,
    left: geometry.lensLeft + offsetX,
    top: geometry.lensTop + offsetY,
    bgWidth: geometry.bgWidth,
    bgHeight: geometry.bgHeight,
    bgPosX: geometry.bgPosX,
    bgPosY: geometry.bgPosY
  }
}

/**
 * `mousemove` dispara dezenas de vezes por segundo e cada passada faz um
 * `getBoundingClientRect` + escrita reativa que repinta a lente. Coalescer por frame deixa no
 * máximo uma atualização por quadro — é o que o olho vê de qualquer forma.
 */
let pendingLensPoint: { clientX: number; clientY: number } | null = null
let lensFrame: number | null = null

function cancelLensFrame(): void {
  if (lensFrame !== null) cancelAnimationFrame(lensFrame)
  lensFrame = null
  pendingLensPoint = null
}

function scheduleLensUpdate(event: MouseEvent): void {
  pendingLensPoint = { clientX: event.clientX, clientY: event.clientY }
  if (lensFrame !== null) return
  lensFrame = requestAnimationFrame(() => {
    lensFrame = null
    const point = pendingLensPoint
    pendingLensPoint = null
    if (point) updateLens(point)
  })
}

function handleMouseMove(event: MouseEvent): void {
  doDragging(event)
  if (isMagnifierActive.value) {
    if (!magnifierSource.value) refreshMagnifierSource()
    scheduleLensUpdate(event)
  }
}

function handleMouseLeave(): void {
  stopDragging()
  cancelLensFrame()
  lens.value = { ...lens.value, visible: false }
}

watch(isMagnifierActive, (on) => {
  if (on) {
    refreshMagnifierSource()
    return
  }
  // Desligada, a lupa não guarda o snapshot da folha: é um data-URL de página inteira.
  cancelLensFrame()
  magnifierSource.value = null
  lens.value = { ...lens.value, visible: false }
})

// Zoom e rotação re-renderizam o canvas: o snapshot da lente precisa acompanhar.
watch([() => props.zoom, () => props.rotation], () => refreshMagnifierSource())

const isDragging = ref(false)
const startX = ref(0)
const startY = ref(0)
const startPanX = ref(0)
const startPanY = ref(0)
const panX = ref(0)
const panY = ref(0)

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max)

const calculateBounds = () => {
  if (!scrollContainer.value || !imageRef.value) return { maxPanX: 0, maxPanY: 0 }

  const img = imageRef.value
  const container = scrollContainer.value

  const isRotated = props.rotation % 180 !== 0
  const imgNaturalWidth = isRotated ? img.naturalHeight : img.naturalWidth
  const imgNaturalHeight = isRotated ? img.naturalWidth : img.naturalHeight

  if (!imgNaturalWidth || !imgNaturalHeight) return { maxPanX: 0, maxPanY: 0 }

  const containerWidth = container.clientWidth
  const containerHeight = container.clientHeight

  const imgRatio = imgNaturalWidth / imgNaturalHeight
  const containerRatio = containerWidth / containerHeight

  let renderedWidth = 0
  let renderedHeight = 0

  if (imgRatio > containerRatio) {
    renderedWidth = containerWidth
    renderedHeight = containerWidth / imgRatio
  } else {
    renderedHeight = containerHeight
    renderedWidth = containerHeight * imgRatio
  }

  const scaledWidth = renderedWidth * props.zoom
  const scaledHeight = renderedHeight * props.zoom

  const maxPanX = Math.max(0, (scaledWidth - containerWidth) / 2)
  const maxPanY = Math.max(0, (scaledHeight - containerHeight) / 2)

  return { maxPanX, maxPanY }
}

const enforceBounds = () => {
  const { maxPanX, maxPanY } = calculateBounds()
  panX.value = clamp(panX.value, -maxPanX, maxPanX)
  panY.value = clamp(panY.value, -maxPanY, maxPanY)
}

const objectUrl = ref<string | null>(null)

watch(
  [() => props.file, () => props.fileUrl],
  ([newFile, newFileUrl]) => {
    currentPage.value = 1
    totalPages.value = 1
    pdfLoadError.value = false
    magnifierSource.value = null

    if (objectUrl.value) {
      URL.revokeObjectURL(objectUrl.value)
      objectUrl.value = null
    }
    if (newFileUrl) {
      previewUrl.value = newFileUrl
    } else if (newFile) {
      const url = URL.createObjectURL(newFile)
      objectUrl.value = url
      previewUrl.value = url
    } else {
      previewUrl.value = null
    }
  },
  { immediate: true }
)

watch(
  () => props.zoom,
  (newZoom) => {
    if (newZoom <= 1) {
      panX.value = 0
      panY.value = 0
    } else {
      nextTick(() => enforceBounds())
    }
  }
)

watch(
  () => props.rotation,
  () => {
    if (props.zoom > 1) {
      nextTick(() => enforceBounds())
    }
  }
)

onUnmounted(() => {
  if (objectUrl.value) URL.revokeObjectURL(objectUrl.value)
  cancelLensFrame()
  magnifierSource.value = null
})

const startDragging = (e: MouseEvent) => {
  if (isPdf.value || props.zoom <= 1) return
  isDragging.value = true
  startX.value = e.clientX
  startY.value = e.clientY
  startPanX.value = panX.value
  startPanY.value = panY.value
}

const stopDragging = () => {
  isDragging.value = false
}

const doDragging = (e: MouseEvent) => {
  if (!isDragging.value) return
  e.preventDefault()

  const walkX = e.clientX - startX.value
  const walkY = e.clientY - startY.value

  const nextPanX = startPanX.value + walkX
  const nextPanY = startPanY.value + walkY

  const { maxPanX, maxPanY } = calculateBounds()

  panX.value = clamp(nextPanX, -maxPanX, maxPanX)
  panY.value = clamp(nextPanY, -maxPanY, maxPanY)
}

const rotateImage = () => {
  emit('update:rotation', (props.rotation + 90) % 360)
}

const zoomIn = () => {
  if (props.zoom < 4) {
    let newZoom = props.zoom + 0.2
    newZoom = Math.min(4, Math.round(newZoom * 10) / 10)
    emit('update:zoom', newZoom)
  }
}

const zoomOut = () => {
  let newZoom = props.zoom
  if (newZoom > 1) {
    newZoom -= 0.2
  } else if (newZoom > 0.4) {
    newZoom -= 0.1
  }

  newZoom = Math.max(0.4, Math.round(newZoom * 10) / 10)
  emit('update:zoom', newZoom)
}

const toggleFullscreen = () => {
  if (!previewContainer.value) return
  if (!document.fullscreenElement) {
    previewContainer.value.requestFullscreen().catch((err) => {
      // fallback intencional: erro local da Fullscreen API do navegador (permissão,
      // iframe), não de API — a pré-visualização segue visível no tamanho normal.
      console.error(`Erro ao tentar ativar modo tela cheia: ${err.message}`)
    })
  } else {
    document.exitFullscreen()
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: _normalizeClass([_ctx.mode === 'preview' ? 'w-full' : 'flex-1', "flex flex-col gap-y-2 min-w-0"])
      }, [
        (_ctx.showDisplayName)
          ? (_openBlock(), _createElementBlock("h3", _hoisted_3, _toDisplayString(displayName.value), 1))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          ref_key: "previewContainer",
          ref: previewContainer,
          "data-testid": "step-preview-viewport",
          class: _normalizeClass(["relative border border-lightGray30 rounded-lg overflow-hidden bg-lightGray30 flex items-center justify-center w-full max-w-full", _ctx.heightPx ? '' : 'min-h-[500px] h-[650px] lg:min-w-[500px]']),
          style: _normalizeStyle(_ctx.heightPx ? { height: `${_ctx.heightPx}px`, minHeight: `${_ctx.heightPx}px` } : undefined)
        }, [
          (!_ctx.hideToolbar)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_component_v_tooltip, {
                  text: "Expandir",
                  location: "left"
                }, {
                  activator: _withCtx(({ props: tooltipProps }) => [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("button", _mergeProps(tooltipProps, {
                        onClick: toggleFullscreen,
                        class: "bg-white w-10 h-8 text-primary30 rounded-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                      }), [
                        _createVNode(_component_v_icon, {
                          icon: _unref(mdiFullscreen),
                          size: "18"
                        }, null, 8, ["icon"])
                      ], 16)
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_tooltip, {
                  text: "Aumentar Zoom",
                  location: "left"
                }, {
                  activator: _withCtx(({ props: tooltipProps }) => [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("button", _mergeProps(tooltipProps, {
                        onClick: zoomIn,
                        class: "bg-white rounded-md w-10 h-8 text-primary30 flex items-center justify-center hover:bg-gray-50 transition-colors"
                      }), [
                        _createVNode(_component_v_icon, {
                          icon: _unref(mdiPlus),
                          size: "18"
                        }, null, 8, ["icon"])
                      ], 16)
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_tooltip, {
                  text: "Diminuir Zoom",
                  location: "left"
                }, {
                  activator: _withCtx(({ props: tooltipProps }) => [
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("button", _mergeProps(tooltipProps, {
                        onClick: zoomOut,
                        class: "bg-white rounded-md w-10 h-8 text-primary30 flex items-center justify-center hover:bg-gray-50 transition-colors"
                      }), [
                        _createVNode(_component_v_icon, {
                          icon: _unref(mdiMinus),
                          size: "18"
                        }, null, 8, ["icon"])
                      ], 16)
                    ])
                  ]),
                  _: 1
                }),
                (_ctx.mode === 'preview')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createVNode(_component_v_tooltip, {
                        text: "Rotacionar imagem",
                        location: "left"
                      }, {
                        activator: _withCtx(({ props: tooltipProps }) => [
                          _createElementVNode("div", _hoisted_8, [
                            _createElementVNode("button", _mergeProps(tooltipProps, {
                              onClick: rotateImage,
                              class: "bg-white rounded-md w-10 h-8 text-primary30 flex items-center justify-center hover:bg-gray-50 transition-colors"
                            }), [
                              _createVNode(_component_v_icon, {
                                icon: _unref(mdiRotateRight),
                                size: "18"
                              }, null, 8, ["icon"])
                            ], 16)
                          ])
                        ]),
                        _: 1
                      }),
                      (_ctx.magnifierToggle)
                        ? (_openBlock(), _createBlock(_component_v_tooltip, {
                            key: 0,
                            text: magnifierTooltip.value,
                            location: "left"
                          }, {
                            activator: _withCtx(({ props: tooltipProps }) => [
                              _createElementVNode("div", _hoisted_9, [
                                _createElementVNode("button", _mergeProps(tooltipProps, {
                                  type: "button",
                                  "data-testid": "ds-attendance-magnifier-toggle",
                                  "aria-pressed": _ctx.magnifier === true,
                                  "aria-label": magnifierTooltip.value,
                                  title: magnifierTooltip.value,
                                  disabled: isMagnifierBlocked.value,
                                  class: ["rounded-md w-10 h-8 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed", 
                        _ctx.magnifier === true
                          ? 'bg-primary30 text-white'
                          : 'bg-white text-primary30 hover:bg-gray-50'
                      ],
                                  onClick: toggleMagnifier
                                }), [
                                  _createVNode(_component_v_icon, {
                                    icon: _unref(mdiMagnify),
                                    size: "18"
                                  }, null, 8, ["icon"])
                                ], 16, _hoisted_10)
                              ])
                            ]),
                            _: 1
                          }, 8, ["text"]))
                        : _createCommentVNode("", true),
                      _createVNode(_component_v_tooltip, {
                        text: "Abrir",
                        location: "left"
                      }, {
                        activator: _withCtx(({ props: tooltipProps }) => [
                          _createElementVNode("div", _hoisted_11, [
                            _createElementVNode("a", _mergeProps(tooltipProps, {
                              href: previewUrl.value ?? undefined,
                              target: "_blank",
                              rel: "noopener noreferrer",
                              class: "bg-white rounded-md w-10 h-8 text-primary30 flex items-center justify-center hover:bg-gray-50 transition-colors"
                            }), [
                              _createVNode(_component_v_icon, {
                                icon: _unref(mdiOpenInNew),
                                size: "18"
                              }, null, 8, ["icon"])
                            ], 16, _hoisted_12)
                          ])
                        ]),
                        _: 1
                      })
                    ], 64))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            ref_key: "scrollContainer",
            ref: scrollContainer,
            class: _normalizeClass(["w-full h-full flex items-center justify-center select-none", [
              isPdf.value ? 'overflow-visible' : 'overflow-hidden',
              !isPdf.value && _ctx.zoom > 1 ? 'cursor-grab active:cursor-grabbing' : ''
            ]]),
            onMousedown: startDragging,
            onMousemove: handleMouseMove,
            onMouseup: stopDragging,
            onMouseleave: handleMouseLeave
          }, [
            (previewUrl.value && !isPdf.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  ref_key: "imageRef",
                  ref: imageRef,
                  src: previewUrl.value,
                  class: _normalizeClass([
                'shadow-lg pointer-events-none ease-out',
                isDragging.value ? 'transition-none' : 'transition-transform duration-300'
              ]),
                  style: _normalizeStyle({
                transform: `translate(${panX.value}px, ${panY.value}px) scale(${_ctx.zoom}) rotate(${_ctx.rotation}deg)`,
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }),
                  onError: _cache[0] || (_cache[0] = ($event: any) => (emit('image-error')))
                }, null, 46, _hoisted_13))
              : (previewUrl.value && isPdf.value)
                ? (_openBlock(), _createBlock(_unref(PdfRenderer), {
                    key: 1,
                    src: previewUrl.value,
                    scale: _ctx.zoom,
                    rotation: _ctx.rotation,
                    page: currentPage.value,
                    "onUpdate:page": _cache[1] || (_cache[1] = ($event: any) => (setPage($event))),
                    onLoaded: _cache[2] || (_cache[2] = 
                ({ totalPages: tp }) => {
                  totalPages.value = tp
                  pdfLoadError.value = false
                  emit('update:totalPages', tp)
                  refreshMagnifierSource()
                }
              ),
                    onError: _cache[3] || (_cache[3] = ($event: any) => (pdfLoadError.value = true))
                  }, null, 8, ["src", "scale", "rotation", "page"]))
                : _createCommentVNode("", true)
          ], 34),
          (isMagnifierActive.value && lens.value.visible && magnifierSource.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                "data-testid": "step-preview-magnifier-lens",
                "aria-hidden": "true",
                class: "pointer-events-none absolute z-20 rounded-md border-2 border-primary30 shadow-lg bg-white bg-no-repeat",
                style: _normalizeStyle({
              left: `${lens.value.left}px`,
              top: `${lens.value.top}px`,
              width: `${_unref(MAGNIFIER_WIDTH)}px`,
              height: `${_unref(MAGNIFIER_HEIGHT)}px`,
              backgroundImage: `url(${magnifierSource.value})`,
              backgroundSize: `${lens.value.bgWidth}px ${lens.value.bgHeight}px`,
              backgroundPosition: `${lens.value.bgPosX}px ${lens.value.bgPosY}px`
            })
              }, null, 4))
            : _createCommentVNode("", true)
        ], 6)
      ], 2),
      (_ctx.mode !== 'preview')
        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
            _hoisted_15,
            _createVNode(OutlinedButton, {
              text: "Rotacionar imagem",
              onClick: rotateImage,
              height: "32px",
              width: "full",
              class: "mt-4",
              "prepend-icon": _unref(mdiRotateRight)
            }, null, 8, ["prepend-icon"]),
            (_ctx.monitoringTypeLabel)
              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                  _hoisted_17,
                  _createElementVNode("span", _hoisted_18, _toDisplayString(_ctx.monitoringTypeLabel), 1)
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})